<template>
  <div class="create-listing-form">

    <form ref="propertyForm">

      <div class="grey-box-container">

        <div class="clearfix">
          <div class="col-23">
            <h2>Property Information</h2>
          </div>
          <div class="col-13" style="text-align: right;">
            <button class="btn-small" @click.prevent.stop="importDialogVisible = true">Import</button>
          </div>
        </div>

        <fieldset>
          <label for="name">Auction name <small>&mdash; if left empty, the address will be used instead</small></label>
          <input type="text" id="name" v-model="auction.name" />
        </fieldset>

        <fieldset>
          <label for="apn">APN *</label>
          <input type="text" id="apn" v-model="auctionMetadata.apn" required />
        </fieldset>

        <div class="clearfix">
          <div class="col-23">
            <fieldset>
              <label for="type">Property type *</label>
              <div class="pfx-selectbox">
                <select id="type" v-model="auction.propertyType" required>
                  <option v-for="type in propertyTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
                </select>
              </div>
            </fieldset>
          </div>

          <div class="col-13">
            <fieldset>
              <label for="yearBuilt">Year built</label>
              <input type="number" id="yearBuilt" v-model="auction.yearBuilt" min="1000" max="2200" />
            </fieldset>
          </div>
        </div>

        <div class="clearfix">
          <fieldset class="col-23">
            <label for="address">Address *</label>
            <input type="text" id="address" v-model="auction.address" required @blur="geocode" />
          </fieldset>

          <fieldset class="col-13">
            <label for="zip">Zipcode *</label>
            <input type="text" id="zip" v-model="auction.zip" required />
          </fieldset>
        </div>

        <div class="clearfix">
          <fieldset class="col-23">
            <label for="city">City *</label>
            <input type="text" id="city" v-model="auction.city" required @blur="geocode" />
          </fieldset>

          <fieldset class="col-13">
            <label for="state">State *</label>
            <div class="pfx-selectbox">
              <select id="state" v-model="auction.state" required>
                <option v-for="state in usStates" :key="state.value" :value="state.value">{{ state.label }}</option>
              </select>
            </div>
          </fieldset>
        </div>

      </div>

      <div class="grey-box-container">

        <h2>Property Description</h2>

        <fieldset>
          <label for="description">Description</label>
          <textarea id="description" v-model="auctionMetadata.description" rows="10" cols="100"></textarea>
        </fieldset>

        <fieldset class="documents" v-if="auctionMetadata.documents && auctionMetadata.documents.length > 0">
          <div><label>Documents</label></div>
          <ul class="list-bullets">
            <li v-for="document in auctionMetadata.documents" :key="document.url" class="list-bullets">
              {{ document.name }}<br/>
              <a target="_blank" :href="document.url">{{ document.url | truncate(80) }}</a>
            </li>
          </ul>
        </fieldset>

        <fieldset v-if="mapsEnabled">
          <div><label>Map Location <small>&mdash; Clicking on the map will set the property location. Use zoom to pin accurately.</small></label></div>
          <!--<div style="float: right; color: #aaa">{{ auctionMetadata.lat }}, {{ auctionMetadata.lng }}</div>-->
          <Map :zoom="16" />
        </fieldset>

      </div>

      <div class="grey-box-container">

        <h2>Property Images</h2>

        <fieldset class="photos">
          <div v-for="photo in auctionMetadata.photos" :key="photo" class="photo" @click="togglePhoto(photo)">
            <img :src="photo" />
            <div v-if="auctionMetadata.photosRemoved.indexOf(photo) > -1" class="removed-overlay">
              <v-icon x-large color="red darken-2" style="position: absolute; top: 50%; transform: translate(-50%, -50%)">delete_forever</v-icon>
            </div>
          </div>
          <div v-for="photo in auctionMetadata.photosAdded" :key="photo.name" class="photo" @click="toggleAddedPhoto(photo)">
            <img :src="photo.dataUrl" />
            <div v-if="photo.removed" class="removed-overlay">
              <v-icon x-large color="red darken-2" style="position: absolute; top: 50%; transform: translate(-50%, -50%)">delete_forever</v-icon>
            </div>
          </div>
          <div v-if="numberOfPhotos === 0">
            <ul class="list-bullets"><li>No photos</li></ul>
          </div>
          <div>
            <AuctionFileInputWidget @uploaded="photoUploaded" />
          </div>
        </fieldset>

      </div>

    </form>

    <v-dialog :lazy="true" v-model="importDialogVisible" max-width="640px">
      <CreateListingImportDialog :selectedId="auctionMetadata.id" @importSelected="importSelected" @closeImportDialog="importDialogVisible = false" />
    </v-dialog>

  </div>
</template>

<script>
  import CreateListingImportDialog from './CreateListingImportDialog.vue'
  import AuctionFileInputWidget from './elements/AuctionFileUploadWidget.vue'
  import Map from './elements/Map.vue'
  import { propertyTypes, usStates } from '../utils/PfxUtils.js'
  import { mapGetters } from 'vuex'

  export default {
    name: 'CreateListingProperty',
    components: {
      Map,
      CreateListingImportDialog,
      AuctionFileInputWidget
    },
    data() {
      return {
        propertyTypes,
        usStates,
        auction: {
          name: '',
          address: '',
          zip: '',
          city: '',
          state: '',
          yearBuilt: null,
          propertyType: ''
        },
        auctionMetadata: {
          apn: '',
          buildingSize: null,
          county: '',
          description: '',
          id: '',
          lat: null,
          lng: null,
          lotSize: null,
          photos: [],
          photosAdded: [],
          photosRemoved: [],
          source: '',
          sourceId: ''
        },
        importDialogVisible: false,
        mapsEnabled: true
      }
    },
    computed: {
      ...mapGetters([
        'isAuctionEdit'
      ]),
      numberOfPhotos() {
        let n = 0
        if (this.auctionMetadata.photos) n += this.auctionMetadata.photos.length
        if (this.auctionMetadata.photosAdded) n += this.auctionMetadata.photosAdded.length
        return n
      }
    },
    watch: {
      auction: {
        deep: true,
        handler() {
          this.$store.commit('setDataToNewAuctionStorage', { auction: this.auction })
        }
      },
      auctionMetadata: {
        deep: true,
        handler() {
          this.$store.commit('setDataToNewAuctionStorage', { auctionMetadata: this.auctionMetadata })
        }
      }
    },
    created() {
      Object.assign(this.auction, this.$store.state.newAuctionStorage.auction)
      Object.assign(this.auctionMetadata, this.$store.state.newAuctionStorage.auctionMetadata)
      this.$store.commit('setDataToNewAuctionStorage', { auction: this.auction })
      this.$store.commit('setDataToNewAuctionStorage', { auctionMetadata: this.auctionMetadata })

      // Include google maps if not already defined
      if (!(window.google && window.google.maps )) {
        this.mapsEnabled = false // Do no render map component until google maps api has been included

        const elem = document.createElement('script')
        elem.setAttribute('src', `https://maps.googleapis.com/maps/api/js?key=${process.env.config.google.apikey}`)
        document.head.appendChild(elem)

        const poll = setInterval(() => {
          if (window.google && window.google.maps) {
            this.mapsEnabled = true
            clearInterval(poll)
          }
        }, 500)
      }
    },
    methods: {

      importSelected(source, imported) {
        console.log('importSelected', source, imported)
        this.selectedId = imported.id

        // process documents array (it contains url<space>name strings)
        const documents = []
        const documentStrings = imported.documents || []
        documentStrings.forEach(s => {
          const splitted = s.trim().split(' ')
          const url = splitted[0]
          const name = splitted.length > 1 ? splitted.slice(1).join(' ') : 'Document'
          documents.push({ url, name })
        })

        const auction = this.$store.state.newAuctionStorage.auction || {}
        auction.name = imported.name
        auction.address = imported.address
        auction.zip = imported.zip
        auction.city = imported.city
        auction.state = imported.state
        auction.yearBuilt = imported.yearBuilt
        auction.propertyType = imported.type
        const auctionMetadata = this.$store.state.newAuctionStorage.auctionMetadata || {}
        auctionMetadata.apn = imported.apn
        auctionMetadata.buildingSize = imported.buildingSize
        auctionMetadata.county = imported.county
        auctionMetadata.description = imported.description
        auctionMetadata.id = imported.id
        auctionMetadata.lat = imported.lat
        auctionMetadata.lng = imported.lng
        auctionMetadata.lotSize = imported.lotSize
        auctionMetadata.photos = imported.photos
        auctionMetadata.documents = documents
        auctionMetadata.source = source
        auctionMetadata.sourceId = imported.sourceId
        auctionMetadata.commissionInfo = imported.commission

        this.importDialogVisible = false
      },

      photoUploaded(json) {
        json.removed = false
        this.auctionMetadata.photosAdded.push(json)
      },

      toggleAddedPhoto(photoAdded) {
        photoAdded.removed = !photoAdded.removed
      },

      togglePhoto(photoUrl) {
        const removedIndex = this.auctionMetadata.photosRemoved.indexOf(photoUrl)
        if (removedIndex === -1) {
          this.auctionMetadata.photosRemoved.push(photoUrl)
        } else {
          this.auctionMetadata.photosRemoved.splice(removedIndex, 1)
        }
      },

      geocode() {
        if (!this.mapsEnabled) return
        if (this.auction.address === '' || this.auction.city === '') return
        const address = this.auction.address + ', ' + this.auction.city
        const Geocoder = new window.google.maps.Geocoder()
        Geocoder.geocode({ address }, (results, status) => {
          console.log('geocode [%s] status [%s]', address, status)
          if (status === 'OK' && Array.isArray(results) && results.length > 0) {
            const result = results[0]
            console.log('geocode [%s] result', address, result)
            if (result.geometry && result.geometry.location) {
              const lat = result.geometry.location.lat()
              const lng = result.geometry.location.lng()
              console.log('geocode [%s] lat [%s] lng [%s]', address, lat, lng)
              this.auctionMetadata.lat = lat
              this.auctionMetadata.lng = lng
            }
          }
        })
      },

      beforeNextStep() {
        if (!this.$refs.propertyForm.checkValidity()) {
          this.$refs.propertyForm.reportValidity()
          return false
        }
        return true
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import 'node_modules/jeet/scss/index';

  .photos {
    .photo {
      position: relative;
      display: inline-block;
      cursor: pointer;
      margin: 0 1rem 0 0;
      padding: 0;

      img {
        height: 10rem;
      }

      .removed-overlay {
        position: absolute; top: 0; bottom: 0; left: 0; right: 0;
        color: #000; background-color: rgba(255,255,255,0.7);
        text-align: center;
      }
    }
  }
  iframe.map {
    width: 100%;
    height: 300px;
    border: 0;
  }
  .col-13 { @include column(1/3); }
  .col-23 { @include column(2/3); }
  .col-12 { @include column(1/2); }
</style>



// WEBPACK FOOTER //
// src/components/CreateListingProperty.vue